import { useCallback, useEffect, useState } from "react";
import { api, nextId } from "@/lib/api";

export function useReporting() {
  const [projects, setProjects] = useState([]);
  const [project, setProject] = useState("all");
  const [messages, setMessages] = useState([]);
  const [history, setHistory] = useState([]);
  const [health, setHealth] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");

  const loadProjects = useCallback(() => api.projects().then(setProjects).catch(() => setNotice("API unavailable — showing workspace preview.")), []);
  const loadHealth = useCallback(() => api.health().then(setHealth).catch(() => setHealth(null)), []);

  useEffect(() => {
    loadProjects();
    loadHealth();
    api.history().then(setHistory).catch(() => {});
    const t = setInterval(() => { loadHealth(); loadProjects(); }, 30000);
    return () => clearInterval(t);
  }, [loadProjects, loadHealth]);

  const ask = useCallback(async text => {
    const prompt = text.trim();
    if (!prompt || busy) return;
    setMessages(prev => [...prev, { id: nextId("u"), role: "user", text: prompt }]);
    setBusy(true);
    setNotice("");
    try {
      const data = await api.ask(prompt, project);
      setMessages(prev => [...prev, { id: nextId("a"), role: "assistant", report: data.report }]);
      setHistory(prev => [{ chat_id: data.chat_id, question: prompt, report_type: data.report.report_type, created_at: data.report.data_timestamp }, ...prev].slice(0, 30));
    } catch (err) {
      setNotice(err?.response?.data?.detail || "I couldn’t reach the reporting service. Please try again.");
    } finally {
      setBusy(false);
    }
  }, [busy, project]);

  const runReport = useCallback(async reportName => {
    const prompt = reportName === "crm" ? "CRM report" : "Loan report";
    if (busy) return;
    setMessages(prev => [...prev, { id: nextId("u"), role: "user", text: prompt }]);
    setBusy(true);
    setNotice("");
    try {
      const report = await api.report(reportName, project);
      setMessages(prev => [...prev, { id: nextId("a"), role: "assistant", report }]);
    } catch (err) {
      setNotice(err?.response?.data?.detail || "I couldn’t reach the reporting service. Please try again.");
    } finally {
      setBusy(false);
    }
  }, [busy, project]);

  const reset = useCallback(() => setMessages([]), []);

  return { projects, project, setProject, messages, history, health, busy, notice, ask, runReport, reset, loadProjects };
}
